"use client";

import { motion } from 'framer-motion';
import { Globe, MapPin, Users, Laptop, Brain, FileCode, ShieldCheck, Bot, Database, Network } from 'lucide-react';

export function AboutTeam() {
  return (
    <section className="py-24 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-h2 mb-6">Das Team</h2>
          <p className="text-body-lg mb-4 max-w-4xl mx-auto">
            Hinter Umynd steht ein Team von Experten aus HR-Tech, KI-Entwicklung und Recruiting.
          </p>
          <div className="w-24 h-1 bg-primary mx-auto rounded-full mb-16" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-card rounded-xl shadow-neumorphic p-8 flex flex-col"
          >
            <div className="bg-primary/10 rounded-full p-4 w-16 h-16 flex items-center justify-center mb-6">
              <Globe className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-h4 mb-4">Globales Team</h3>
            <p className="text-body mb-4 flex-grow">
              Unser Kernteam in Deutschland wird durch Experten auf drei Kontinenten erweitert. Diese globale Perspektive erlaubt es uns, die besten Talente und vielfältiges Know-how zu vereinen.
            </p>
            <div className="flex items-center gap-2 text-primary font-medium">
              <Users className="w-5 h-5" />
              <span>Experten in Europa, Asien und Nordamerika</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-card rounded-xl shadow-neumorphic p-8 flex flex-col"
          >
            <div className="bg-primary/10 rounded-full p-4 w-16 h-16 flex items-center justify-center mb-6">
              <MapPin className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-h4 mb-4">Moderne Zusammenarbeit</h3>
            <p className="text-body mb-4 flex-grow">
              Mit unserem Firmensitz in Köln verbinden wir lokale Präsenz mit einem zukunftsorientierten Arbeitsmodell. Wir leben flexible Zusammenarbeit durch Remote-Arbeit und Workation-Möglichkeiten.
            </p>
            <div className="flex items-center gap-2 text-primary font-medium">
              <Laptop className="w-5 h-5" />
              <span>Remote-First mit lokaler Verankerung</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-card rounded-xl shadow-neumorphic p-8 flex flex-col"
          >
            <div className="bg-primary/10 rounded-full p-4 w-16 h-16 flex items-center justify-center mb-6">
              <Users className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-h4 mb-4">HR-Expertise</h3>
            <p className="text-body mb-4 flex-grow">
              Unser Team bringt jahrzehntelange Erfahrung in der HR-Branche mit. Von operativer Personalarbeit über strategisches Recruiting bis hin zu HR-Tech - wir kennen die Herausforderungen aus erster Hand.
            </p>
            <div className="flex items-center gap-2 text-primary font-medium">
              <ShieldCheck className="w-5 h-5" />
              <span>20+ Jahre HR-Branchenerfahrung</span>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-card rounded-xl shadow-neumorphic p-8 mb-16"
        >
          <div className="flex items-center mb-8">
            <div className="bg-primary/10 rounded-full p-4 w-16 h-16 flex items-center justify-center mr-6">
              <Brain className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-h3">Unsere KI-Kompetenzen</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-start">
              <div className="bg-primary/10 rounded-full p-2 mr-3 flex-shrink-0">
                <Bot className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h4 className="font-bold mb-1">Generative KI & LLMs</h4>
                <p className="text-sm text-muted-foreground">Pionierarbeit mit Large Language Models und multimodalen KI-Systemen für die HR-Domäne</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-primary/10 rounded-full p-2 mr-3 flex-shrink-0">
                <Network className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h4 className="font-bold mb-1">Multi-Agent-Systeme</h4>
                <p className="text-sm text-muted-foreground">Entwicklung spezialisierter KI-Agenten, die im Team zusammenarbeiten und verschiedene Recruiting-Aufgaben übernehmen</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-primary/10 rounded-full p-2 mr-3 flex-shrink-0">
                <FileCode className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h4 className="font-bold mb-1">Prompt Engineering</h4>
                <p className="text-sm text-muted-foreground">Hochspezialisierte Prompt-Entwicklung für HR-spezifische Aufgaben und nuancierte Kandidatenkommunikation</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-primary/10 rounded-full p-2 mr-3 flex-shrink-0">
                <Database className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h4 className="font-bold mb-1">Semantische Suche</h4>
                <p className="text-sm text-muted-foreground">Entwicklung fortschrittlicher semantischer Matching-Algorithmen für präzise Kandidaten-Stellenprofil-Zuordnungen</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-primary/10 rounded-full p-2 mr-3 flex-shrink-0">
                <Brain className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h4 className="font-bold mb-1">Fine-Tuning & RAG</h4>
                <p className="text-sm text-muted-foreground">Anpassung von KI-Modellen für die spezifischen Anforderungen der Personalarbeit und Integration von Retrieval-Augmented Generation</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-primary/10 rounded-full p-2 mr-3 flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h4 className="font-bold mb-1">Verantwortungsvolle KI</h4>
                <p className="text-sm text-muted-foreground">Entwicklung und Implementierung von Fairness-Algorithmen und ethischen Rahmenwerken für Bias-freies Recruiting</p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center"
        >
          <h3 className="text-h3 mb-6">Unsere Teamkultur</h3>
          <p className="text-body-lg max-w-4xl mx-auto">
            Bei Umynd verbinden wir technologische Exzellenz mit einer menschenzentrierten Unternehmenskultur.
            Wir glauben, dass die besten Lösungen dort entstehen, wo Diversität, Vertrauen und mutige Innovation zusammenkommen.
          </p>
        </motion.div>
      </div>
    </section>
  );
} 